import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Clock, EmptyState, Menu, Modal, Zap } from '@opendate-app/setlist';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import {
  challengesUrgency,
  fetchHoldChallenges,
  formatRespondBy,
  holdChallengeErrorMessage,
  isUrgent,
  keepHoldChallenge,
  soonestRespondBy,
  timeLeftLabel,
  withdrawHoldChallenge
} from '../api/holdChallengesApi';
import '../styles/holdChallenges.css';

const TICK_MS = 60 * 1000;
const COMPACT_MODAL_WIDTH = 'min(440px, calc(100vw - 32px))';

const formatHoldDate = (date) =>
  new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const challengeLocation = (challenge) =>
  [formatHoldDate(challenge.date), ...(challenge.room_names || [])].join(' · ');

const stopMenuKeyboardNavigationExceptEscape = (event) => {
  if (event.key !== 'Escape') event.stopPropagation();
};

const HoldLink = ({ hold }) => (
  <>
    <span className='setlist-font-semibold'>{`H${hold.position}`}</span>
    {' '}
    <a href={hold.url} className='hold-challenges-hold-link'>{hold.title}</a>
  </>
);

HoldLink.propTypes = {
  hold: PropTypes.shape({
    position: PropTypes.number.isRequired,
    title: PropTypes.string,
    url: PropTypes.string
  }).isRequired
};

const ChallengeCard = ({ challenge, now, busy, canManage, canRelease, onGoTo, onKeep, onRelease, onWithdraw }) => {
  const { challenger, challenged } = challenge;

  return (
    <div className='hold-challenges-item'>
      <div className='hold-challenges-item-row'>
        <Button variant='link-color' size='sm' className='hold-challenges-date' onClick={() => onGoTo(challenge)}>
          {challengeLocation(challenge)}
        </Button>
        <span className='hold-challenges-time-left'>
          <Badge size='sm' color={isUrgent(challenge.respond_by, now) ? 'error' : 'warning'} icon={Clock}>
            {timeLeftLabel(challenge.respond_by, now)}
          </Badge>
        </span>
      </div>
      <div className='setlist-text-sm hold-challenges-who'>
        <HoldLink hold={challenger} />
        {challenged ? (
          <>
            {' is challenging '}
            <HoldLink hold={challenged} />
          </>
        ) : ' is waiting on the next hold in line'}
      </div>
      {challenged && (
        <div className='setlist-text-xs hold-challenges-muted'>
          {`H${challenged.position} ${challenge.overdue ? 'was due' : 'must respond by'} ${formatRespondBy(challenge.respond_by)}`}
        </div>
      )}
      {canManage && (
        <div className='hold-challenges-item-actions'>
          {challenged && (
            <Button variant='secondary' size='sm' disabled={busy} onClick={() => onKeep(challenge)}>
              {`H${challenged.position} confirmed`}
            </Button>
          )}
          {challenged && canRelease && challenged.clearable && (
            <Button variant='secondary' size='sm' disabled={busy} onClick={() => onRelease(challenge)}>
              {`H${challenged.position} released`}
            </Button>
          )}
          <Button
            variant='link-destructive'
            size='sm'
            disabled={busy}
            className='hold-challenges-withdraw'
            onClick={() => onWithdraw(challenge)}
          >
            Withdraw
          </Button>
        </div>
      )}
    </div>
  );
};

ChallengeCard.propTypes = {
  challenge: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  busy: PropTypes.bool.isRequired,
  canManage: PropTypes.bool.isRequired,
  canRelease: PropTypes.bool.isRequired,
  onGoTo: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired,
  onRelease: PropTypes.func.isRequired,
  onWithdraw: PropTypes.func.isRequired
};

const HoldChallengesMenu = ({
  team,
  canManageHoldChallenges,
  canClearHolds,
  refreshKey,
  onGoToChallenge,
  onReleaseHold,
  onChallengesChanged,
  compact
}) => {
  const [challenges, setChallenges] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const loadChallenges = useCallback(() => {
    fetchHoldChallenges(team)
      .then((data) => {
        setChallenges(data);
        setNow(Date.now());
      })
      .catch(() => toastError('Could not load challenges. Please try again.'))
      .finally(() => setLoaded(true));
  }, [team]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges, refreshKey]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const timer = setTimeout(() => panelRef.current?.querySelector('button, a[href]')?.focus());
    return () => clearTimeout(timer);
  }, [open]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadChallenges();
  };

  const release = (challenge) => {
    setOpen(false);
    onReleaseHold(challenge);
  };

  const goTo = (challenge) => {
    if (compact) setOpen(false);
    onGoToChallenge(challenge);
  };

  const setBusy = (id, busy) => setBusyIds((current) => {
    const next = new Set(current);
    if (busy) next.add(id); else next.delete(id);
    return next;
  });

  const closeChallenge = (challenge, request, successMessage, fallbackError) => {
    setBusy(challenge.id, true);
    request(team, challenge.id)
      .then(() => {
        toastSuccess(successMessage);
        setChallenges((current) => current.filter((c) => c.id !== challenge.id));
        onChallengesChanged();
      })
      .catch((error) => toastError(holdChallengeErrorMessage(error, fallbackError)))
      .finally(() => setBusy(challenge.id, false));
  };

  const keep = (challenge) => closeChallenge(
    challenge,
    keepHoldChallenge,
    `Challenge closed. H${challenge.challenged.position} kept the date.`,
    'Could not close the challenge.'
  );

  const withdraw = (challenge) => closeChallenge(
    challenge,
    withdrawHoldChallenge,
    'Challenge withdrawn',
    'Could not withdraw the challenge.'
  );

  const urgency = challengesUrgency(challenges, now);
  const urgencyBadgeLabel = urgency === 'urgent'
    ? `${challenges.length} · ${timeLeftLabel(soonestRespondBy(challenges), now)}`
    : `${challenges.length}`;

  const panel = (
    <div ref={panelRef} className={`hold-challenges-panel${compact ? ' hold-challenges-panel--compact' : ''}`} onKeyDown={compact ? undefined : stopMenuKeyboardNavigationExceptEscape}>
      {!compact && (
        <div className='hold-challenges-panel-head'>
          <div className='setlist-text-sm setlist-font-semibold'>Active challenges</div>
          <div className='setlist-text-xs hold-challenges-muted'>Soonest deadline first</div>
        </div>
      )}
      {loaded && challenges.length === 0 ? (
        <div className='hold-challenges-empty'>
          <EmptyState
            size='sm'
            icon={Zap}
            showRings={false}
            title='No active challenges'
            description={compact
              ? <>Tap <strong>Challenge</strong> on a hold with another hold ahead of it to start a challenge.</>
              : 'Hover over a hold with another hold ahead of it and click its ⚡ to start a challenge.'}
          />
        </div>
      ) : challenges.map((challenge) => (
        <ChallengeCard
          key={challenge.id}
          challenge={challenge}
          now={now}
          busy={busyIds.has(challenge.id)}
          canManage={canManageHoldChallenges}
          canRelease={canClearHolds}
          onGoTo={goTo}
          onKeep={keep}
          onRelease={release}
          onWithdraw={withdraw}
        />
      ))}
      {canManageHoldChallenges && challenges.some((challenge) => challenge.challenged) && (
        <div className='hold-challenges-panel-foot setlist-text-xs'>
          &ldquo;Confirmed&rdquo; closes the challenge and leaves the challenger where it is.
          &ldquo;Released&rdquo; clears the challenged hold and moves the challenger up.
        </div>
      )}
    </div>
  );

  const urgencyBadge = urgency !== 'idle' && (
    <span className='hold-challenges-button-badge'>
      <Badge size='sm' color={urgency === 'urgent' ? 'error' : 'warning'}>
        {compact ? `${challenges.length}` : urgencyBadgeLabel}
      </Badge>
    </span>
  );

  const triggerProps = {
    variant: 'secondary',
    size: compact ? 'md' : 'sm',
    icon: Zap,
    className: `hold-challenges-button${compact ? ' hold-challenges-button--compact' : ''}`,
    'data-urgency': urgency,
    'aria-label': `Challenges${challenges.length > 0 ? `: ${challenges.length} active` : ''}`
  };

  if (compact) {
    return (
      <>
        <Button {...triggerProps} onClick={() => handleOpenChange(true)}>
          {urgencyBadge}
        </Button>
        {open && (
          <Modal
            open
            onClose={() => setOpen(false)}
            icon={false}
            width={COMPACT_MODAL_WIDTH}
            title='Active challenges'
            description='Soonest deadline first'
          >
            {panel}
          </Modal>
        )}
      </>
    );
  }

  return (
    <Menu
      placement='bottom-end'
      minWidth={440}
      className='hold-challenges-menu'
      aria-label='Active challenges'
      open={open}
      onOpenChange={handleOpenChange}
      header={panel}
      items={[]}
      trigger={(
        <Button {...triggerProps}>
          Challenges
          {urgencyBadge}
        </Button>
      )}
    />
  );
};

HoldChallengesMenu.propTypes = {
  team: PropTypes.object.isRequired,
  canManageHoldChallenges: PropTypes.bool.isRequired,
  canClearHolds: PropTypes.bool.isRequired,
  refreshKey: PropTypes.any,
  onGoToChallenge: PropTypes.func.isRequired,
  onReleaseHold: PropTypes.func.isRequired,
  onChallengesChanged: PropTypes.func.isRequired,
  compact: PropTypes.bool
};

HoldChallengesMenu.defaultProps = {
  compact: false
};

export default HoldChallengesMenu;
